/**
 * Desktop profiles of a tenant (docs/vdi/desktop-profile.md), one section per
 * kind: further Windows desktops (own VMs, e.g. "Windows Office" and "Windows
 * Entwicklung") or Linux desktops (own pool, e.g. "Linux CAD"), each with its
 * own Keycloak group. Fixed slots 1-4 per kind, at most 8 machines each; keys
 * unique across both kinds. Saving replaces the set of this kind and starts the
 * playbook of that kind (every other setting stays). Mounted in VdiSection
 * while VDI is enabled.
 */
import { useEffect, useState } from 'react';
import { VdiProfile, getTenantVdiProfiles, saveTenantVdiProfiles, extractApiError } from '../services/api';

interface Props {
  slug: string;
  kind: 'linux' | 'windows';
  isReadOnly: boolean;
}

const SLOTS = [1, 2, 3, 4];
const KEY_RE = /^[a-z][a-z0-9]{1,10}$/;
const GROUP_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;

/** First problem of the edited set, or null (same rules as the backend). */
export function profilesProblem(rows: VdiProfile[]): string | null {
  for (const r of rows) {
    if (!KEY_RE.test(r.key) || r.key === 'linux' || r.key === 'windows') return `Schlüssel „${r.key}“: 2–11 Zeichen a–z/0–9, beginnt mit Buchstabe`;
    if (!r.label.trim() || r.label.length > 60) return `Profil ${r.key}: Name 1–60 Zeichen`;
    if (!GROUP_RE.test(r.group)) return `Profil ${r.key}: Keycloak-Gruppe ungültig`;
    if (!Number.isInteger(r.maxDesktops) || r.maxDesktops < 1 || r.maxDesktops > 8) return `Profil ${r.key}: 1–8 VMs`;
    if (!Number.isInteger(r.minPool) || r.minPool < 0 || r.minPool > r.maxDesktops) return `Profil ${r.key}: Warm-Pool 0–Anzahl VMs`;
  }
  if (new Set(rows.map((r) => r.key)).size !== rows.length) return 'Zwei Profile haben denselben Schlüssel';
  if (new Set(rows.map((r) => r.slot)).size !== rows.length) return 'Zwei Profile belegen denselben Platz';
  return null;
}

export function VdiProfilesSection({ slug, kind, isReadOnly }: Props) {
  const isWin = kind === 'windows';
  const [rows, setRows] = useState<VdiProfile[]>([]);
  const [saved, setSaved] = useState<VdiProfile[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getTenantVdiProfiles(slug).then((all) => {
      if (cancelled) return;
      const p = all.filter((r) => (r.kind ?? 'windows') === kind);
      setRows(p); setSaved(p); setLoaded(true);
    }).catch(() => {
      if (!cancelled) { setErr('Desktop-Profile konnten nicht geladen werden'); setLoaded(true); }
    });
    return () => { cancelled = true; };
  }, [slug, kind]);

  const freeSlot = SLOTS.find((s) => !rows.some((r) => r.slot === s));
  const problem = profilesProblem(rows);
  const changed = JSON.stringify(rows) !== JSON.stringify(saved);

  function update(i: number, patch: Partial<VdiProfile>): void {
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  }

  function add(): void {
    if (freeSlot === undefined) return;
    setRows((rs) => [...rs, { slot: freeSlot, key: '', label: '', group: '', maxDesktops: 1, minPool: 0 }]);
  }

  async function save(): Promise<void> {
    if (isReadOnly || problem) return;
    const removed = saved.filter((s) => !rows.some((r) => r.slot === s.slot));
    const added = rows.filter((r) => !saved.some((s) => s.slot === r.slot));
    const note = (isWin ? [
      added.length ? `Neue Profile klonen ${added.reduce((n, r) => n + r.maxDesktops, 0)} VM(s), je rund 35 Minuten.` : '',
      removed.length ? 'Entfernte Profile verschwinden aus dem Portal; ihre VMs bleiben angehalten stehen und werden nicht gelöscht.' : '',
      'Jeder Windows-Nutzer kostet eine RDS-SAL.',
    ] : [
      added.length ? 'Neue Profile bekommen einen eigenen Desktop-Pool und ein eigenes Home je Nutzer.' : '',
      removed.length ? 'Entfernte Profile verlieren ihren Pool; die Homes auf dem Profilserver bleiben.' : '',
    ]).filter(Boolean).join('\n');
    if (!window.confirm(`Desktop-Profile übernehmen?\n\n${note}`)) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await saveTenantVdiProfiles(slug, kind, rows);
      const p = r.profiles.filter((x) => (x.kind ?? 'windows') === kind);
      setRows(p); setSaved(p);
      setMsg(`Übernahme gestartet (AWX-Job #${r.jobId}).`);
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setBusy(false);
    }
  }

  if (!loaded) return <p className="text-slate-500 text-sm">Lade Desktop-Profile…</p>;

  return (
    <div className="bg-white rounded shadow p-4 mt-4">
      <h4 className="text-sm font-semibold text-slate-600 mb-1">Desktop-Profile ({isWin ? 'Windows' : 'Linux'})</h4>
      <p className="text-xs text-slate-500 mb-3">
        {isWin
          ? 'Weitere Windows-Desktops neben dem Standard-Pool, je mit eigener Keycloak-Gruppe und eigenen VMs. Windows muss für den Mandanten bereits eingeschaltet sein.'
          : 'Weitere Linux-Desktops neben dem Standard-Pool, je mit eigener Keycloak-Gruppe, eigenem Pool und eigenem Home.'}
        {' '}Nutzer sehen im Portal jedes Profil, dessen Gruppe sie haben. Höchstens 4 Profile mit je 8 Desktops;
        Schlüssel sind über Linux und Windows eindeutig.
      </p>
      {err && <div className="bg-red-100 text-red-700 p-2 rounded mb-2 text-sm">{err}</div>}
      {msg && <div className="bg-green-100 text-green-700 p-2 rounded mb-2 text-sm">{msg}</div>}
      {rows.length === 0 && <p className="text-sm text-slate-500 mb-2">Keine Profile.</p>}
      {rows.length > 0 && (
        <table className="w-full text-sm mb-2">
          <thead>
            <tr className="text-left text-slate-500">
              <th className="pr-2">Platz</th><th className="pr-2">Schlüssel</th><th className="pr-2">Name</th>
              <th className="pr-2">Keycloak-Gruppe</th><th className="pr-2">{isWin ? 'VMs' : 'Desktops'}</th><th className="pr-2">Warm-Pool</th><th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.slot}>
                <td className="pr-2 font-mono">p{r.slot}</td>
                <td className="pr-2">
                  <input aria-label={`Schlüssel p${r.slot}`} value={r.key} disabled={isReadOnly || saved.some((s) => s.slot === r.slot)}
                    onChange={(e) => update(i, { key: e.target.value })} className="w-24 px-1 py-0.5 rounded border font-mono" />
                </td>
                <td className="pr-2">
                  <input aria-label={`Name p${r.slot}`} value={r.label} disabled={isReadOnly}
                    onChange={(e) => update(i, { label: e.target.value })} className="w-40 px-1 py-0.5 rounded border" />
                </td>
                <td className="pr-2">
                  <input aria-label={`Gruppe p${r.slot}`} value={r.group} disabled={isReadOnly}
                    onChange={(e) => update(i, { group: e.target.value })} className="w-36 px-1 py-0.5 rounded border font-mono" />
                </td>
                <td className="pr-2">
                  <input aria-label={`VMs p${r.slot}`} type="number" min={1} max={8} value={r.maxDesktops} disabled={isReadOnly}
                    onChange={(e) => update(i, { maxDesktops: Number(e.target.value) })} className="w-14 px-1 py-0.5 rounded border font-mono" />
                </td>
                <td className="pr-2">
                  <input aria-label={`Warm-Pool p${r.slot}`} type="number" min={0} max={8} value={r.minPool} disabled={isReadOnly}
                    onChange={(e) => update(i, { minPool: Number(e.target.value) })} className="w-14 px-1 py-0.5 rounded border font-mono" />
                </td>
                <td>
                  {!isReadOnly && (
                    <button onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} className="text-red-600 text-xs">entfernen</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {problem && rows.length > 0 && <p className="text-xs text-red-600 mb-2">{problem}</p>}
      {!isReadOnly && (
        <div className="flex gap-2">
          <button onClick={add} disabled={busy || freeSlot === undefined} className="btn-secondary disabled:opacity-50">Profil hinzufügen</button>
          <button onClick={() => void save()} disabled={busy || !changed || Boolean(problem)} className="btn-primary disabled:opacity-50">
            Profile übernehmen
          </button>
        </div>
      )}
      <p className="text-xs text-slate-400 mt-2">
        Der Schlüssel ist nach dem Anlegen fest (er steht in den Stream-Adressen). Zentral (Realm sectepe) gehören die
        Gruppen in kubernetes/auth/vdi-central-keycloak-groups.yaml.
      </p>
    </div>
  );
}
